/**
 * Helpers for dropping library items into a playlist at the position the
 * operator actually dropped them, instead of always appending to the bottom.
 *
 * The drop position is resolved here as a 0-based list index; the add mutations
 * take it as a 1-based `startPlayOrder` and shift existing items down server
 * side, so a positioned drop stays a single request.
 */

/**
 * Insert index for a pointer position, resolved against the rendered item
 * cards inside `container`. Items are marked with `data-playlist-item`.
 *
 * Returns the index the dragged items should land on: the first card whose
 * vertical midpoint is below the pointer, or the end of the list when the
 * pointer sits past every card. `null` means "position unknown" — the caller
 * should fall back to appending.
 */
export const getInsertIndexFromPointer = (
  container: HTMLElement | null,
  offset: { x: number; y: number } | null,
): number | null => {
  if (!container || !offset) return null;

  const cards = Array.from(
    container.querySelectorAll<HTMLElement>("[data-playlist-item]"),
  );

  for (let index = 0; index < cards.length; index++) {
    const rect = cards[index].getBoundingClientRect();
    if (offset.y < rect.top + rect.height / 2) return index;
  }

  return cards.length;
};

/**
 * True when two assignment orders are identical. Used to skip the reorder
 * request (and its toast) when an item is dropped back where it started.
 */
export const isSameOrder = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * Converts a 0-based drop index into the 1-based `startPlayOrder` the add
 * mutations expect. `null` (no resolved position) means "append", which the
 * mutations express by leaving the argument out.
 */
export const toStartPlayOrder = (
  insertIndex: number | null,
): number | undefined => (insertIndex === null ? undefined : insertIndex + 1);
